import { useCallback, useEffect, useRef } from 'react';
import './clippy.css';

interface AnimationDef {
  name: string;
  frames: Array<[x: number, y: number]>;
  frameDuration: number;
}

const FRAME_W = 124;
const FRAME_H = 93;
const SPRITE_URL = '/clippy/Clippy/map.png';

const IDLE: AnimationDef = {
  name: 'Idle',
  frames: [[0, 0], [124, 0], [248, 0]],
  frameDuration: 600,
};

const ANIMATIONS: Record<string, AnimationDef> = {
  Idle: IDLE,
  Show: { name: 'Show', frames: [[372, 0], [496, 0]], frameDuration: 250 },
  Hide: { name: 'Hide', frames: [[620, 0], [744, 0]], frameDuration: 300 },
  Wave: { name: 'Wave', frames: [[868, 0], [992, 0], [1116, 0]], frameDuration: 250 },
  Greeting: { name: 'Greeting', frames: [[0, 93], [124, 93], [248, 93]], frameDuration: 280 },
  LookLeft: { name: 'LookLeft', frames: [[372, 93], [496, 93]], frameDuration: 300 },
  LookRight: { name: 'LookRight', frames: [[620, 93], [744, 93]], frameDuration: 300 },
  LookDown: { name: 'LookDown', frames: [[868, 93], [992, 93]], frameDuration: 300 },
};

const CLICK_POOL = ['Wave', 'Greeting'];
const HOVER_POOL = ['LookLeft', 'LookRight', 'LookDown'];
const HOVER_THROTTLE_MS = 1500;

export interface ClippyHostProps {
  hidden?: boolean;
  soundEnabled?: boolean;
  interactive?: boolean;
  /** When true, the sprite renders static (frame 0) and only animates on interaction. */
  idleDisabled?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
}

export function ClippyHost({
  hidden = false,
  interactive = true,
  idleDisabled = false,
  onClick,
  onDoubleClick,
}: ClippyHostProps): null {
  const elRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef(0);
  const animRef = useRef<AnimationDef>(IDLE);
  const queueRef = useRef<AnimationDef[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastHoverRef = useRef(0);
  const onClickRef = useRef(onClick);
  const onDoubleClickRef = useRef(onDoubleClick);
  const interactiveRef = useRef(interactive);
  const idleDisabledRef = useRef(idleDisabled);
  const mountedRef = useRef(false);

  useEffect(() => { onClickRef.current = onClick; }, [onClick]);
  useEffect(() => { onDoubleClickRef.current = onDoubleClick; }, [onDoubleClick]);
  useEffect(() => { interactiveRef.current = interactive; }, [interactive]);
  useEffect(() => { idleDisabledRef.current = idleDisabled; }, [idleDisabled]);

  const drawFrame = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const anim = animRef.current;
    const idx = frameRef.current;
    const coord = anim.frames[idx];
    if (coord) {
      el.style.backgroundPosition = `${-coord[0]}px ${-coord[1]}px`;
    }
  }, []);

  const tick = useCallback(() => {
    const anim = animRef.current;
    if (anim.name === 'Idle' && idleDisabledRef.current && queueRef.current.length === 0) return;
    frameRef.current++;
    if (frameRef.current >= anim.frames.length) {
      const next = queueRef.current.shift();
      if (next) {
        animRef.current = next;
        frameRef.current = 0;
      } else {
        animRef.current = IDLE;
        frameRef.current = 0;
      }
    }
    drawFrame();
  }, [drawFrame]);

  const playAnim = useCallback((name: string) => {
    const anim = ANIMATIONS[name];
    if (!anim) return;
    queueRef.current.push(anim);
    if (animRef.current.name === 'Idle') {
      const next = queueRef.current.shift();
      if (next) {
        animRef.current = next;
        frameRef.current = 0;
        drawFrame();
      }
    }
  }, [drawFrame]);

  // Mount/unmount the sprite element on document.body
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const el = document.createElement('div');
    el.className = 'clippy';
    el.style.width = `${FRAME_W}px`;
    el.style.height = `${FRAME_H}px`;
    el.style.backgroundImage = `url('${SPRITE_URL}')`;
    el.style.backgroundRepeat = 'no-repeat';
    el.style.cursor = 'pointer';
    el.style.pointerEvents = 'auto';
    document.body.appendChild(el);
    elRef.current = el;

    // Initial draw
    frameRef.current = 0;
    animRef.current = IDLE;
    drawFrame();

    // Start the animation loop
    timerRef.current = setInterval(tick, IDLE.frameDuration);

    // Interaction listeners
    el.addEventListener('mouseenter', () => {
      if (!interactiveRef.current) return;
      const now = Date.now();
      if (now - lastHoverRef.current < HOVER_THROTTLE_MS) return;
      lastHoverRef.current = now;
      const pick = HOVER_POOL[Math.floor(Math.random() * HOVER_POOL.length)]!;
      playAnim(pick);
    });

    el.addEventListener('click', () => {
      if (!interactiveRef.current) return;
      const pick = CLICK_POOL[Math.floor(Math.random() * CLICK_POOL.length)]!;
      playAnim(pick);
      onClickRef.current?.();
    });

    el.addEventListener('dblclick', () => {
      if (!interactiveRef.current) return;
      const keys = Object.keys(ANIMATIONS).filter(k => k !== 'Idle');
      const pick = keys[Math.floor(Math.random() * keys.length)]!;
      playAnim(pick);
      onDoubleClickRef.current?.();
    });

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      el.remove();
      elRef.current = null;
      mountedRef.current = false;
    };
  }, [drawFrame, tick, playAnim]);

  // Hidden toggle
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    el.style.display = hidden ? 'none' : '';
  }, [hidden]);

  return null;
}
